<template functional>
  <div
    class="relative flex flex-col justify-end"
    :class="$options.methods.mergeFunctionalRenderContextClasses(data)"
  >
    <slot name="label">
      <label
        v-if="props.label"
        :for="`${props.label}_select`"
        class="mb-1 mr-2 tracking-wider text-gray-800"
        v-text="props.label"
      />
    </slot>
    <slot :id="`${props.label}_input`">
      <mia-select
        :id="`${props.label}_select`"
        v-bind="data.attrs"
        :name="`${props.label}_select`"
        class="form-select"
        :class="{
          'text-gray-500': $options.methods.isEmpty(data.attrs.value),
          'has-errors': props.errors.length > 0
        }"
        v-on="{
          ...$options.methods.getFilteredListeners(listeners),
          change: (event) => {
            if (listeners.input) {
              listeners.input(event.target.value)
            }
          },
          input: (event) => {
            if (listeners.modelInput) {
              listeners.modelInput(event)
            }

            if (listeners.input) {
              listeners.input(event)
            }
          }
        }"
      >
        <slot
          name="options"
          :value="data.attrs.value"
        >
          <slot
            name="default-option"
            :placeholder="props.placeholder || (props.label ? `${props.label} wählen` : 'Bitte wählen')"
          >
            <option
              value=""
              :disabled="props.disableDefaultOption"
              class="text-gray-500"
              v-text="props.placeholder || (props.label ? `${props.label} wählen` : 'Bitte wählen')"
            />
          </slot>
          <option
            v-for="option in props.options"
            :key="props.isFlat ? option : option[props.idBy]"
            class="text-black"
            :value="props.isFlat ? option : option[props.trackBy]"
            v-text="props.isFlat ? option : (typeof props.nameBy === 'function' ? props.nameBy(option) : option[props.nameBy])"
          />
        </slot>
      </mia-select>
    </slot>

    <mia-fade-transition>
      <small
        v-if="props.errors.length > 0"
        class="absolute top-0 block leading-3 text-red-600 mt-17"
        v-text="props.errors[0]"
      />
    </mia-fade-transition>
  </div>
</template>

<script>
import { mergeFunctionalRenderContextClasses } from '@/helpers/utils'

export default {
  model: {
    // Having a v-model binding, but also providing an @input event listener
    // to do additional things when the input changes results in the following binding for "input":
    // input: [null, null]. This seems to be a restriction when using functional components. Therefore
    // we have to account for that and use an alias instead.
    event: 'modelInput'
  },

  props: {
    label: {
      type: String,
      default: ''
    },

    options: {
      type: Array,
      default() {
        return []
      }
    },

    placeholder: {
      type: String,
      default: ''
    },

    idBy: {
      type: String,
      default: 'id'
    },

    trackBy: {
      type: String,
      default: 'name'
    },

    isFlat: {
      type: Boolean,
      default: false
    },

    nameBy: {
      type: [String, Function],
      default: 'description'
    },

    errors: {
      type: Array,
      default() {
        return []
      }
    },

    disableDefaultOption: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    mergeFunctionalRenderContextClasses,

    isEmpty(value) {
      if (typeof value === 'string') {
        return value === ''
      }

      return value === null || value === undefined
    },

    /**
     * Exclude the "modelInput" listener, if present, since that is a made up event listener name
     * which functions as alias for the "input" event, in order to workaround the issue described above.
     */
    getFilteredListeners(listeners) {
      const filteredListeners = {}

      Object.entries(listeners).forEach(([key, listener]) => {
        if (key !== 'modelInput') {
          filteredListeners[key] = listener
        }
      })

      return filteredListeners
    }
  }
}
</script>

<style lang="scss" scoped>
.form-select {
  &:required,
  &.is-required {
    border-left-width: calc(theme('width.px') * 3);
    border-left-color: theme('borderColor.orange-mia');
  }

  &.has-errors {
    @apply shadow-outline-error;
  }
}
</style>
